import { create } from "zustand";
import { autoSaveManager } from "../services/auto-save";
import { projectManager } from "../services/project-manager";
import { subscribeWithSelector } from "zustand/middleware";
import {
  deleteAccount,
  fetchMe,
  fetchSessions,
  logout,
  patchOnboarding,
  removeAvatar,
  requestEmailOtp,
  requestMagicLink,
  verifyEmailOtp,
  revokeAllSessions,
  revokeSession,
  startGoogleOAuth,
  updateProfile,
} from "../services/auth/client";
import {
  isUnauthorized,
  toAuthError,
  type AuthError,
  type AuthSession,
  type AuthSessionRecord,
  type AuthUser,
  type EmailOtpVerifyResult,
  type OnboardingState,
} from "../services/auth/types";

/**
 * Session state for the app.
 *
 * Note what is missing: there is no `persist` middleware here, unlike every
 * other store in this folder. Auth state is derived from an HttpOnly cookie
 * the server owns, so the server is the only source of truth. Writing a copy
 * of the user into localStorage would give the app a signed in looking shell
 * after the session was revoked, and would put an email address in a place any
 * injected script can read. On every load we ask the server instead.
 */

export type AuthStatus = "loading" | "authed" | "anon";

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  onboarding: OnboardingState | null;
  /**
   * Last failure, cleared on the next success. A non null `error` while
   * `status` is "anon" means "we could not ask", not "you are signed out":
   * check `error.kind === "network"` before telling the user anything.
   */
  error: AuthError | null;
  /** True once the first /auth/me round trip has settled, success or not. */
  hydrated: boolean;
  /**
   * True only when the last verify was what created the account. It is a routing
   * hint for the onboarding flow, not an access decision, and it is cleared on
   * sign out. `false` for a returning user and for a hydrate that found an
   * existing session.
   */
  isNewUser: boolean;

  /** Runs the one time /auth/me on app start. Never throws. */
  hydrate: () => Promise<void>;
  /** Forces a fresh /auth/me. Use this as the retry after a network error. */
  refresh: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  /** Emails a 6-digit code. Resolves the same whether or not the address exists. */
  requestEmailOtp: (email: string) => Promise<void>;
  /**
   * Trades a code for a session and populates the store on success. Returns the
   * verify result so a caller can read `isNewUser` inline; the same flag is also
   * mirrored into state. Throws on a wrong/expired code (400) or too many
   * attempts (429) so the sign-in UI can phrase the right message.
   */
  verifyEmailOtp: (email: string, code: string) => Promise<EmailOtpVerifyResult>;
  updateOnboarding: (partial: Partial<OnboardingState>) => Promise<OnboardingState>;
  /** Always ends in local "anon". Never throws, see the note at the call site. */
  signOut: () => Promise<void>;

  /**
   * Active sessions. Not part of hydration: this is a settings surface, so it
   * is fetched when the panel opens and never kept warm in the background.
   * `null` means "not fetched yet", which is different from "no sessions".
   */
  sessions: AuthSessionRecord[] | null;
  sessionsLoading: boolean;

  loadSessions: () => Promise<void>;
  updateProfile: (partial: {
    firstName?: string | null;
    lastName?: string | null;
  }) => Promise<AuthUser>;
  removeAvatar: () => Promise<AuthUser>;
  /** Revoking the current session signs this browser out too. */
  revokeSession: (sessionId: string) => Promise<void>;
  revokeAllSessions: () => Promise<void>;
  deleteAccount: (confirmEmail: string) => Promise<void>;
}

/** Deduplicates concurrent hydrate calls from multiple mounted components. */
let inflightHydrate: Promise<void> | null = null;

const ANONYMOUS = {
  status: "anon" as const,
  user: null,
  onboarding: null,
  isNewUser: false,
};

function sessionPatch(session: AuthSession | null): Partial<AuthState> {
  if (!session) {
    return { ...ANONYMOUS, error: null, hydrated: true };
  }
  return {
    status: "authed",
    user: session.user,
    onboarding: session.onboarding,
    // A session found by /auth/me is by definition not one this verify created.
    isNewUser: false,
    error: null,
    hydrated: true,
  };
}

/**
 * Turn a thrown value into a state patch.
 *
 * The only thing that de-authenticates is a 401, which is the server saying
 * the session is not valid. A timeout, an offline device or a 500 leaves the
 * session question unanswered, so whatever we already knew about the user
 * survives untouched.
 *
 * The one adjustment: if we were still in "loading" when the request failed,
 * we settle on "anon" so the UI is not stuck behind a spinner forever. No user
 * was signed in at that point, so nobody is being logged out, and `error`
 * carries the reason so the UI can offer a retry instead of a sign in wall.
 */
function failurePatch(current: AuthState, thrown: unknown): Partial<AuthState> {
  const error = toAuthError(thrown);
  if (isUnauthorized(error)) {
    return { ...ANONYMOUS, error, hydrated: true };
  }
  return {
    status: current.status === "loading" ? "anon" : current.status,
    error,
    hydrated: true,
  };
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    status: "loading",
    user: null,
    onboarding: null,
    error: null,
    hydrated: false,
    isNewUser: false,

    hydrate: async () => {
      if (get().hydrated) return;
      if (inflightHydrate) return inflightHydrate;

      inflightHydrate = (async () => {
        try {
          set(sessionPatch(await fetchMe()));
        } catch (thrown) {
          set(failurePatch(get(), thrown));
        } finally {
          inflightHydrate = null;
        }
      })();

      return inflightHydrate;
    },

    refresh: async () => {
      try {
        set(sessionPatch(await fetchMe()));
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    signInWithGoogle: async () => {
      try {
        // On success this navigates away and nothing after it runs.
        await startGoogleOAuth();
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    sendMagicLink: async (email: string) => {
      try {
        await requestMagicLink(email);
        set({ error: null });
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    requestEmailOtp: async (email: string) => {
      try {
        await requestEmailOtp(email);
        set({ error: null });
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    verifyEmailOtp: async (email: string, code: string) => {
      try {
        const result = await verifyEmailOtp(email, code);
        // The server set the session cookie on that response. Mirror the session
        // into the store so the gate re-renders straight past the sign-in screen.
        set({
          status: "authed",
          user: result.user,
          onboarding: result.onboarding,
          isNewUser: result.isNewUser,
          error: null,
          hydrated: true,
        });
        return result;
      } catch (thrown) {
        // A bad code is a 400/429, never a 401, so failurePatch leaves any
        // existing session untouched and just records the error for the UI.
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    updateOnboarding: async (partial: Partial<OnboardingState>) => {
      try {
        const onboarding = await patchOnboarding(partial);
        set({ onboarding, error: null });
        return onboarding;
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    sessions: null,
    sessionsLoading: false,

    loadSessions: async () => {
      set({ sessionsLoading: true });
      try {
        set({ sessions: await fetchSessions(), error: null });
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      } finally {
        set({ sessionsLoading: false });
      }
    },

    updateProfile: async (partial) => {
      try {
        const user = await updateProfile(partial);
        set({ user, error: null });
        return user;
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    removeAvatar: async () => {
      try {
        const user = await removeAvatar();
        set({ user, error: null });
        return user;
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
    },

    revokeSession: async (sessionId: string) => {
      // Read the row before the call, so the "was that me?" question is answered
      // against the list the user was actually looking at.
      const endedCurrent =
        get().sessions?.find((row) => row.id === sessionId)?.current === true;

      try {
        await revokeSession(sessionId);
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }

      if (endedCurrent) {
        // The server cleared the cookies with that response. Anything short of
        // going anonymous here would leave a signed-in looking shell over a
        // session that no longer exists.
        set({ ...ANONYMOUS, sessions: null, error: null, hydrated: true });
        return;
      }

      set({
        sessions: get().sessions?.filter((row) => row.id !== sessionId) ?? null,
        error: null,
      });
    },

    revokeAllSessions: async () => {
      try {
        await revokeAllSessions();
      } catch (thrown) {
        set(failurePatch(get(), thrown));
        throw thrown;
      }
      // Every session went, this one included. See the client for why.
      set({ ...ANONYMOUS, sessions: null, error: null, hydrated: true });
    },

    deleteAccount: async (confirmEmail: string) => {
      try {
        await deleteAccount(confirmEmail);
      } catch (thrown) {
        // Deliberately does NOT clear local state. A failed delete leaves the
        // account alive, and showing a signed-out app over a live account would
        // tell the user their data is gone when it is not.
        set(failurePatch(get(), thrown));
        throw thrown;
      }
      set({ ...ANONYMOUS, sessions: null, error: null, hydrated: true });
    },

    signOut: async () => {
      try {
        await logout();
        set({ ...ANONYMOUS, sessions: null, error: null, hydrated: true });
      } catch (thrown) {
        // Deliberate exception to the rule above. The user asked to sign out,
        // so the local state clears either way and this action does not throw.
        //
        // Known gap: if the request never landed, the server session and its
        // cookie are still alive, so a reload signs them back in. `error` is
        // set so the UI can say the sign out did not reach the server rather
        // than claiming success.
        set({ ...ANONYMOUS, sessions: null, error: toAuthError(thrown), hydrated: true });
      }
    },
  })),
);

/**
 * Keep local project storage pointed at the signed-in account.
 *
 * Projects live in IndexedDB on the device, so without this every account
 * signing in on a shared browser sees every other account's work. Subscribing
 * here rather than in a component means it follows sign-in, sign-out and
 * hydration alike, and cannot be missed by a screen that forgot to mount.
 */
useAuthStore.subscribe(
  (state) => state.user?.id ?? null,
  (ownerId) => {
    void projectManager.setOwner(ownerId);
    void autoSaveManager.setOwner(ownerId);
  },
  { fireImmediately: true },
);

/**
 * Kick off hydration from outside React (app bootstrap, a route loader).
 * Safe to call more than once, the store deduplicates.
 */
export function initAuth(): Promise<void> {
  return useAuthStore.getState().hydrate();
}

/** Test only: drop back to a pristine, un-hydrated store. */
export function resetAuthStore(): void {
  inflightHydrate = null;
  useAuthStore.setState({
    status: "loading",
    user: null,
    onboarding: null,
    error: null,
    hydrated: false,
    isNewUser: false,
    sessions: null,
    sessionsLoading: false,
  });
}
